<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <Menu/>

          <div class="element-box mt-4">
            <div class="row mt-2">
              <CoachingButton
                  :enabled="companySettings?.socioTherapyCoachingEnabled"
                  route-name="socioTherapyCoachingNewAndActiveList"
                  coaching-type="socioTherapy"
                  label="Soziotherapie"/>
              <CoachingButton
                  :enabled="companySettings?.assistedLivingCoachingEnabled"
                  route-name="assistedLivingCoachingNewAndActiveList"
                  coaching-type="assistedLiving"
                  label="Betreutes Wohnen"/>
            </div>
          </div>

          <div>
            <h6 class="mt-5">{{ $t('dashboard.customer_information') }}</h6>
            <div class="row mt-4">
              <div class="col-lg-3 col-md-6 col-sm-3">
                <CardInfo :label="$t('general.active_customers')" :value="dashboardData?.countActiveCoaching?.total"/>
              </div>
              <div class="col-lg-3 col-md-6 col-sm-3">
                <CardInfo :label="$t('general.new_customers')" :value="dashboardData?.countNewCoaching?.total"/>
              </div>
              <div class="col-lg-3 col-md-6 col-sm-3">
                <CardInfo :label="'Summe FLS pro Woche'" :value="dashboardData?.sumUnitsInWeekForAlCoaching"/>
              </div>
            </div>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6 class="mt-3">
              Geleistete Einheiten im Monat
            </h6>

            <div class="row mt-4">
              <div class="col-lg-3 col-md-4 col-sm-12">
                <SelectVue
                    v-model="filters.coachingType"
                    :options="dropdownCoachingType"
                    :is-clear="false"
                />
              </div>
              <div class="col-lg-2 col-md-4 col-sm-12">
                <SelectVue
                    v-model="filters.year"
                    placeholder="Standort"
                    :options="dropdownYears"
                    :is-clear="true"
                />
              </div>
              <div class="col-lg-4 col-md-4 col-sm-12">
                <SelectVue
                    v-model="filters.locationId"
                    placeholder="Standort"
                    :options="dropdownOffices"
                    :is-clear="true"
                />
              </div>

              <div class="col-auto text-center">
                <button class="btn btn-primary custom_search_button2" @click="getChartBarArea()">
                      <span v-if="isSearching">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {{ $t('general.button.search_in_progress') }}
                      </span>
                  <span v-else>
                        {{ $t('general.button.search') }}
                      </span>
                </button>
              </div>
            </div>
            <VueApexCharts width="100%" height="450" type="bar" :options="chartOptionsBar" :series="seriesSignBar"/>
          </div>
        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar Infos
          </h6>
        </template>

        <div class="element-box pie mt-4">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts width="100%" type="donut" :options="chartOptions" :series="seriesActiveCoach"/>
        </div>

        <div class="element-box pie">
          <h6>{{ $t('general.new_customers') }}</h6>
          <VueApexCharts width="100%" type="donut" :options="chartOptionsNewCoach" :series="seriesNewCoach"/>
        </div>

      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardHeadPsychosocialServicesService from '@/api/Dashboard/HeadPsychosocialServices';
import CardInfo from '@/components/card/CardInfo.vue';
import CoachingButton from '@/components/button/CoachingButton.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import SelectVue from '@/components/SelectVue.vue';
import { ErrorToast } from '@/components/VueToast';
import { getCoachingTypes, getOfficeDropdown, getYears } from '@/shared/dropdowns';
import { optionsChartBar, optionsChartDonut } from '@/shared/utils/apex';
import { MonthsUtils } from '@/shared/utils/MonthsUtils';
import { piniaCompanySettingsStore } from '@/store/piniaCompanySettingsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import Menu from '@/views/App/Dashboard/HeadPsychosocialServices/Menu.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import VueApexCharts from 'vue3-apexcharts';

const { t }                = useI18n();
const dropdownYears        = ref([]);
const dropdownCoachingType = ref([]);
const dropdownOffices      = ref([]);

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const dashboardData = ref({});

const seriesActiveCoach = ref([]);
const seriesNewCoach    = ref([]);
const seriesSignBar     = ref([]);
const isSearching       = ref(false);

const chartOptions         = ref(optionsChartDonut);
const chartOptionsNewCoach = ref(optionsChartDonut);

const chartOptionsBar = ref({
  ...optionsChartBar,
  yaxis: {
    min: 0,
    max: 1000 + 10,
    forceNiceScale: true,
  },
  xaxis: {
    categories: MonthsUtils(t),
  },
  colors: [
    'rgba(67, 173, 78, 0.6)',
    'rgba(75, 192, 192, 0.6)',
  ],
  plotOptions: {
    bar: {
      distributed: false,
    },
  },
});

const filters = ref({
  coachingType: 'socio_therapy_coaching',
  year: new Date().getFullYear(),
  locationId: '',
});

async function getInfoData() {
  try {
    const { data } = await dashboardHeadPsychosocialServicesService.getInfoData();

    dashboardData.value     = data.data;
    seriesActiveCoach.value = Object.values(data.data.countActiveCoaching?.counts);
    seriesNewCoach.value    = Object.values(data.data.countNewCoaching?.counts);

    chartOptions.value = {
      ...chartOptions.value,
      responsive: [ {
        breakpoint: 2480,
        options: {
          chart: {
            width: '100%',
            height: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      } ],
      labels: [
        `Soziotherapie ${data.data.countActiveCoaching?.counts.socioTherapyCoaching}`,
        `Betreutes Wohnen ${data.data.countActiveCoaching?.counts.assistedLivingCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => data.data.countActiveCoaching.total.toString(),
              },
            },
          },
        },
      },
    };

    chartOptionsNewCoach.value = {
      ...chartOptions.value,
      responsive: [ {
        breakpoint: 2480,
        options: {
          chart: {
            width: '100%',
            height: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      } ],
      labels: [
        `Soziotherapie ${data.data.countNewCoaching?.counts.socioTherapyCoaching}`,
        `Betreutes Wohnen ${data.data.countNewCoaching?.counts.assistedLivingCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => data.data.countNewCoaching.total.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getChartBarArea() {
  try {
    isSearching.value = true;

    const { data } =
        await dashboardHeadPsychosocialServicesService.getUnitsDoneChart(filters.value);

    seriesSignBar.value = [
      data.data.chartData.signedUnits,
      data.data.chartData.unsignedUnits,
    ];

    const allValues = [
      ...data.data.chartData.signedUnits.data,
      ...data.data.chartData.unsignedUnits.data,
    ];

    const maxValue = Math.max(...allValues);

    const niceMax = Math.ceil(maxValue / 10) * 10;

    chartOptionsBar.value = {
      ...chartOptionsBar.value,
      chart: {
        ...chartOptionsBar.value.chart,
        height: 450,
      },
      yaxis: {
        min: 0,
        max: niceMax,
        tickAmount: 5,
      },
    };

    isSearching.value = false;
  } catch (error) {
    isSearching.value = false;
    await ErrorToast(error);
  }
}

onMounted(() => {
  getInfoData();
  getChartBarArea();
  getYears(dropdownYears);
  getCoachingTypes(dropdownCoachingType, [
    'group_coaching',
    'private_learning_coaching',
    'job_coaching',
    'occupational_therapy_coaching',
    'private_coaching',
    'case_management_coaching',
  ]);
  getOfficeDropdown(dropdownOffices);
});

</script>

<style scoped>
::v-deep(.pie .apexcharts-legend) {
  display: block !important;
}
</style>